import React, {useEffect, useId, useLayoutEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {useIntl} from 'react-intl'

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** Keyboard-reachable elements inside `root`, in DOM order, skipping hidden ones. */
const focusableIn = (root: HTMLElement): HTMLElement[] =>
  Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.getClientRects().length > 0
  )

/**
 * A dialog or popup opened over the sheet (a time picker, a delete confirmation): portaled to
 * <body> after it, so it follows the sheet in document order. That one owns Escape and Tab.
 */
const hasDialogAbove = (own: HTMLElement | null): boolean => {
  if (!own) return false
  return Array.from(
    document.querySelectorAll<HTMLElement>('[role="dialog"], [role="alertdialog"]')
  ).some(
    (el) =>
      el !== own &&
      !own.contains(el) &&
      Boolean(own.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) &&
      el.getClientRects().length > 0
  )
}

interface SheetModalProps {
  show: boolean
  onHide: () => void
  title: string
  icon?: string
  elementIcon?: React.ReactNode
  children: React.ReactNode
  showCloseButton?: boolean
  /** Sheet width cap; the sheet still shrinks to 100% on narrow viewports. */
  maxWidth?: string
  /** Extra class(es) on the sheet card, for surface restyling by the host. */
  sheetClassName?: string
  /**
   * Squeeze the page instead of dimming it: while open, `sheet-push-open` is set
   * on <body> with `--sheet-push-width` = maxWidth, and the backdrop is removed
   * from hit-testing so the page beside the sheet keeps scrolling (a transparent
   * backdrop still swallowed wheel events; a long review page was stuck). No
   * click-to-close in this mode — the sheet's own close/cancel controls do that.
   * Hosts opt in and provide the matching CSS (see .onboarding-main-content in
   * _onboardingwizard.scss).
   */
  push?: boolean
  /**
   * Opt-in dialog semantics: named by `title`, focus moves into the sheet on open (unless the
   * content already took it) and back to the opener on close, Tab/Shift+Tab stay inside, and
   * Escape calls `onHide`. A popup opened over the sheet keeps its own Escape/Tab. Hosts that do
   * not pass it keep the previous behaviour.
   */
  accessibleDialog?: boolean
}

export const SheetModal: React.FC<SheetModalProps> = ({
  show,
  onHide,
  title,
  icon = 'fa-calendar-alt',
  children,
  elementIcon,
  showCloseButton = false,
  maxWidth = '560px',
  sheetClassName,
  push = false,
  accessibleDialog = false,
}) => {
  const intl = useIntl()
  const [isOpen, setIsOpen] = useState(false)
  const titleId = useId()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const sheetRef = useRef<HTMLDivElement>(null)
  // Latest onHide for the key handler, which is bound once per open.
  const onHideRef = useRef(onHide)
  useEffect(() => {
    onHideRef.current = onHide
  })

  // Accessible dialog: focus in on open, back to the opener on close; Tab cycles inside the
  // sheet (in push mode the page beside it stays visible but is not in the Tab order); Escape
  // closes. Child effects run first, so content that focuses its own heading on mount keeps it.
  // The opener is read in a layout effect: by the passive effect below, the content may already
  // have moved focus inside the sheet.
  const openerRef = useRef<HTMLElement | null>(null)
  useLayoutEffect(() => {
    if (accessibleDialog && show) openerRef.current = document.activeElement as HTMLElement | null
  }, [accessibleDialog, show])
  useEffect(() => {
    if (!accessibleDialog || !show) return
    const opener = openerRef.current
    const sheet = sheetRef.current
    if (sheet && !sheet.contains(document.activeElement)) sheet.focus({preventScroll: true})

    const onKeyDown = (e: KeyboardEvent) => {
      const sheetEl = sheetRef.current
      if (!sheetEl || e.defaultPrevented || hasDialogAbove(wrapperRef.current)) return
      if (e.key === 'Escape') {
        e.preventDefault()
        onHideRef.current()
        return
      }
      if (e.key !== 'Tab') return
      const focusables = focusableIn(sheetEl)
      const active = document.activeElement
      if (focusables.length === 0) {
        e.preventDefault()
        sheetEl.focus({preventScroll: true})
        return
      }
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (!sheetEl.contains(active)) {
        e.preventDefault()
        ;(e.shiftKey ? last : first).focus()
      } else if (e.shiftKey && (active === first || active === sheetEl)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      if (opener?.isConnected) opener.focus({preventScroll: true})
    }
  }, [accessibleDialog, show])

  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => setIsOpen(true), 10)
      return () => clearTimeout(timer)
    } else {
      setIsOpen(false)
    }
  }, [show])

  // Push mode: squeeze the host page for the sheet's width while it is open.
  useEffect(() => {
    if (!push || !show) return
    document.body.classList.add('sheet-push-open')
    document.body.style.setProperty('--sheet-push-width', maxWidth)
    return () => {
      document.body.classList.remove('sheet-push-open')
      document.body.style.removeProperty('--sheet-push-width')
    }
  }, [push, show, maxWidth])

  if (!show) return null

  return createPortal(
    <div
      ref={wrapperRef}
      role='dialog'
      aria-modal='true'
      aria-labelledby={accessibleDialog && title ? titleId : undefined}
      className='position-fixed'
      style={{
        inset: 0,
        zIndex: 1055,
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'flex-end',
        // Push mode: the wrapper and backdrop let pointer/wheel events through to the
        // page; only the sheet itself catches them (see below).
        pointerEvents: push ? 'none' : 'auto',
      }}
    >
      {/* Backdrop overlay */}
      <div
        className='position-fixed frequency-edit-modal-backdrop'
        onClick={push ? undefined : onHide}
        style={{
          inset: 0,
          background: 'var(--bs-backdrop-bg)',
          zIndex: 1050,
          opacity: push ? 0 : isOpen ? 0.3 : 0,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: push ? 'none' : 'auto',
        }}
      />
      {/* Sheet container */}
      <div
        ref={sheetRef}
        // Focus target on open when the content does not take focus itself.
        tabIndex={accessibleDialog ? -1 : undefined}
        className={`card card-xl-stretch frequency-edit-modal-sheet${
          sheetClassName ? ` ${sheetClassName}` : ''
        }`}
        style={{
          ...(accessibleDialog ? {outline: 'none'} : {}),
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          width: '100%',
          maxWidth,
          zIndex: 1060,
          display: 'flex',
          flexDirection: 'column',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
          margin: 0,
          borderRadius: 0,
          pointerEvents: 'auto',
        }}
      >
        <div className='card-header d-flex align-items-center justify-content-between border-bottom'>
          <div className='d-flex align-items-center'>
            {icon && <i className={`fas ${icon} me-2`}></i>}
            {elementIcon && <span className='me-2'>{elementIcon}</span>}
            <span id={titleId} className='fw-bold'>
              {title}
            </span>
          </div>
          {showCloseButton && (
            <button
              type='button'
              className='btn btn-sm btn-icon btn-light'
              onClick={onHide}
              aria-label={intl.formatMessage({id: 'GENERAL.CLOSE'})}
            >
              <i className='fas fa-times'></i>
            </button>
          )}
        </div>
        <div
          className='card-body pt-0'
          style={{
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            flex: 1,
            minHeight: 0,
          }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  )
}
